import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useApolloClient } from '@apollo/client/react';
import { Loader2, Home, Briefcase, CreditCard } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Button, Separator, cn } from '@scio3/ui';
import { GET_APP_CFG } from '@scio3/graphql-client';
import { PLAN_FEATURES, BILLING_INTERVAL } from '@scio3/shared-types';
import { useSubscriptionWizard, STEP } from '../hooks/useSubscriptionWizard';
import type { WizardStep } from '../hooks/useSubscriptionWizard';
import { getPlanDisplayName } from '../utils/plan-helpers';
import { useTeamStore } from '@/stores/team-store';
import { ProcessingOverlay } from '../components/ProcessingOverlay';
import { ScreenManagementDialog } from './ScreenManagementDialog';
import { ChangePlanWarningDialog } from './wizard/ChangePlanWarningDialog';
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog';
import { WizardStepScreens } from './wizard/WizardStepScreens';
import { WizardStepPlan } from './wizard/WizardStepPlan';
import { WizardStepPaymentTerm } from './wizard/WizardStepPaymentTerm';
import { WizardStepConfirmation } from './wizard/WizardStepConfirmation';
import { WizardStepFinish } from './wizard/WizardStepFinish';

// ---------------------------------------------------------------------------
// Step indicator config
// ---------------------------------------------------------------------------

const STEP_INDICATORS = [
  { step: STEP.NUMBER_SCREEN, icon: Home, label: 'Number of Screens' },
  { step: STEP.PLAN, icon: Briefcase, label: 'Select Plan' },
  { step: STEP.CONFIRMATION, icon: CreditCard, label: 'Payment method' },
] as const;

/** Map wizard step to step indicator index (0-2) */
function getIndicatorIndex(step: WizardStep): number {
  switch (step) {
    case STEP.NUMBER_SCREEN:
      return 0;
    case STEP.PLAN:
      return 1;
    case STEP.PAYMENT_TERM:
    case STEP.CONFIRMATION:
      return 2;
    case STEP.FINISH:
      return 3;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SubscriptionWizardDialog() {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const w = useSubscriptionWizard();
  const activeIndicatorIndex = getIndicatorIndex(w.step);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  // Load AppCfg to check if TypeForm cancel subscription is configured
  const { data: appCfgData } = useQuery<{ getAppCfgs: Record<string, unknown> }>(GET_APP_CFG, {
    variables: { teamId: activeTeamId, appType: 'scio' },
    skip: !activeTeamId || !w.isOpen,
    fetchPolicy: 'cache-and-network',
  });
  const typeFormCancelEmbed = appCfgData?.getAppCfgs?.typeFormCancelSubscriptionEmbed as
    | string
    | undefined;

  // Show "Cancel Subscription" button — matching scio1 conditions:
  // 1. TypeForm embed configured, 2. has screens, 3. not trial, 4. not free plan, 5. not team billing
  const showCancelButton =
    !w.contextTeamId &&
    !!typeFormCancelEmbed &&
    (w.maxScreens ?? 0) > 0 &&
    !w.isTrial &&
    w.currentPlanFeature !== PLAN_FEATURES.FREE_PLAN;

  // ---- Render step content ----
  const renderStepContent = () => {
    switch (w.step) {
      case STEP.NUMBER_SCREEN:
        return (
          <WizardStepScreens
            screenCount={w.screenCount}
            onScreenCountChange={w.setScreenCount}
            minScreens={w.minScreens}
            planInfoText={w.planInfoText}
            isPaid={!w.isTrial}
            planName={w.aliasData?.name ?? getPlanDisplayName(w.currentPlanFeature ?? 'free')}
            planPriceText={`${w.currencySymbol}${w.currentPricePerScreen.toFixed(2)}/screen/${w.billingInterval === BILLING_INTERVAL.YEARLY ? t('per year') : t('per month')}`}
            onChangePlan={w.skipPlanStep ? undefined : () => w.setStep(STEP.PLAN)}
            onDecreaseBlocked={() => w.setShowScreenManagement(true)}
          />
        );

      case STEP.PLAN:
        return (
          <WizardStepPlan
            selectedPlan={w.selectedPlan}
            onPlanChange={w.setSelectedPlan}
            onSelectAndNext={(plan) => {
              w.setSelectedPlan(plan);
              w.goNext();
            }}
            onSelectFree={w.handleSelectFree}
            currentPlan={w.currentPlanFeature ?? undefined}
            currency={w.currency}
            onCurrencyChange={w.setCurrency}
            currencyDisabled={!w.isTrial}
            minPlanRequired={w.contextMinPlan}
          />
        );

      case STEP.PAYMENT_TERM:
        return (
          <WizardStepPaymentTerm
            billingInterval={w.billingInterval}
            onBillingIntervalChange={w.setBillingInterval}
            monthlyPrice={w.monthlyPrice}
            yearlyPrice={w.yearlyPrice}
            screenCount={w.screenCount}
            currency={w.currency}
          />
        );

      case STEP.CONFIRMATION:
        return (
          <WizardStepConfirmation
            isTrial={w.isTrial}
            existingCard={w.existingCard}
            useExistingCard={w.useExistingCard}
            onUseExistingCardChange={w.setUseExistingCard}
            stripeLoading={w.stripeLoading}
            stripeClientSecret={w.stripeClientSecret}
            stripePaymentMethodOrder={w.stripePaymentMethodOrder}
            stripeReturnUrl={w.stripeReturnUrl}
            defaultEmail={w.defaultEmail}
            onStripeSuccess={w.handleStripeSuccess}
            isSubmitting={w.isSubmitting}
            billingInterval={w.billingInterval}
            currentPricePerScreen={w.currentPricePerScreen}
            screenCount={w.screenCount}
            onScreenCountChange={w.setScreenCount}
            minScreens={w.minScreens}
            originalScreenCount={w.maxScreens}
            disableScreenCount={!!w.contextMinPlan}
            addonTotal={w.addonTotal}
            addonItems={w.addonItems}
            currency={w.currency}
            selectedPlan={w.selectedPlan}
            planEditName={
              w.aliasPlanData?.[w.currency]?.[w.billingInterval]?.name as string | undefined
            }
            planEditDiscount={
              w.aliasPlanData?.[w.currency]?.[w.billingInterval]?.discount as number | undefined
            }
            reviewResult={w.reviewResult}
            reviewLoading={w.reviewLoading}
            initialCredit={w.initialCredit}
            onConfirmExistingCard={w.handleConfirmExistingCard}
            promoCode={w.promoCode}
            onPromoCodeChange={w.setPromoCode}
            showPromoCode={w.showPromoCode}
            onShowPromoCode={w.setShowPromoCode}
            onBillingAddressChange={w.handleBillingAddressChange}
            onStripeSubmitStart={() => w.setStripeProcessing(true)}
          />
        );

      case STEP.FINISH:
        return (
          <WizardStepFinish
            chargeResult={w.chargeResult as Parameters<typeof WizardStepFinish>[0]['chargeResult']}
            currency={w.currency}
            currencySymbol={w.currencySymbol}
            currentCard={w.existingCard}
            onClose={() => w.handleOpenChange(false)}
          />
        );
    }
  };

  return (
    <Dialog open={w.isOpen} onOpenChange={w.handleOpenChange}>
      <DialogContent
        className="max-h-[85vh] flex flex-col overflow-hidden sm:max-w-5xl p-0"
        data-testid="subscription-wizard-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="px-6 pt-6 pb-4 space-y-1">
          <DialogTitle>
            {w.contextMinPlan ? t('Upgrade Required') : t('Update Subscription Plan')}
          </DialogTitle>
          {w.contextMinPlan && w.step === STEP.PLAN && (
            <p className="text-sm text-muted-foreground">
              {t('This feature requires at least the {{plan}} plan', {
                plan: w.aliasData?.name ?? getPlanDisplayName(w.contextMinPlan),
              })}
              {w.monthlyPrice > 0 &&
                `: ${w.currencySymbol}${w.monthlyPrice.toFixed(2)}/${t('mo/device')}`}
              .
            </p>
          )}
        </DialogHeader>

        {/* Step indicator — only shown for trial users, hidden on FINISH */}
        {w.step !== STEP.FINISH && w.isTrial && (
          <div className="px-6 pt-8 pb-4">
            <div className="flex items-center justify-between max-w-lg mx-auto">
              {STEP_INDICATORS.map((indicator, idx) => {
                const Icon = indicator.icon;
                const isDone = activeIndicatorIndex > idx;
                const isCurrent = activeIndicatorIndex === idx;
                const isDisabled = activeIndicatorIndex < idx;

                return (
                  <div key={indicator.step} className="flex flex-col items-center relative flex-1">
                    {/* Connecting line (before icon, except first) */}
                    {idx > 0 && (
                      <div
                        className={cn(
                          'absolute top-5 right-1/2 h-0.5 w-full -translate-y-1/2 z-0',
                          isDone || isCurrent ? 'bg-primary' : 'bg-border',
                        )}
                      />
                    )}

                    {/* Icon circle */}
                    <div
                      className={cn(
                        'relative z-10 flex items-center justify-center rounded-full border-2 h-10 w-10',
                        isCurrent
                          ? 'border-primary bg-primary text-white'
                          : isDone
                            ? 'border-primary bg-primary text-white'
                            : 'border-border bg-card text-muted-foreground',
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </div>

                    {/* Label */}
                    <span
                      className={cn(
                        'mt-2 text-xs text-center',
                        isCurrent
                          ? 'text-primary font-medium'
                          : isDisabled
                            ? 'text-muted-foreground'
                            : 'text-foreground',
                      )}
                    >
                      {t(indicator.label)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <ProcessingOverlay visible={w.isSubmitting} />

        {/* Step content — show loader while sub-account data loads */}
        <div className="flex-1 overflow-y-auto px-6">
          {w.loadingSubAccountData ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            renderStepContent()
          )}
        </div>

        {/* Footer with navigation */}
        <Separator />
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2">
            {showCancelButton && w.step === STEP.NUMBER_SCREEN && (
              <Button
                variant="outline"
                className="cursor-pointer text-destructive border-destructive hover:bg-destructive/10"
                onClick={() => setShowCancelDialog(true)}
              >
                {t('Cancel Subscription')}
              </Button>
            )}
            {w.stepIndex > 0 &&
              w.step !== STEP.FINISH &&
              !(w.contextMinPlan && w.step === STEP.PLAN) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!w.isTrial && w.step === STEP.CONFIRMATION) {
                      if (w.skippedToConfirmation) {
                        w.setSkippedToConfirmation(false);
                        w.setStep(STEP.NUMBER_SCREEN);
                      } else {
                        w.setStep(STEP.PAYMENT_TERM);
                      }
                    } else {
                      w.goBack();
                    }
                  }}
                  className="cursor-pointer"
                >
                  {t('Previous')}
                </Button>
              )}
          </div>
          <div className="flex items-center gap-2">
            {w.step === STEP.FINISH ? null : w.step === STEP.CONFIRMATION &&
              !w.isTrial ? null : w.step === STEP.CONFIRMATION ? (
              w.useExistingCard ? (
                <Button
                  onClick={w.handleConfirmExistingCard}
                  disabled={w.isSubmitting || w.reviewLoading}
                  className={cn(
                    w.isSubmitting || w.reviewLoading
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer',
                  )}
                >
                  {w.isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      {t('Processing...')}
                    </>
                  ) : (
                    t('Update subscription')
                  )}
                </Button>
              ) : (
                <Button
                  type="submit"
                  form="stripe-payment-form"
                  disabled={w.isSubmitting || w.stripeProcessing || !w.stripeClientSecret}
                  className={cn(
                    w.isSubmitting || w.stripeProcessing || !w.stripeClientSecret
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer',
                  )}
                >
                  {w.isSubmitting || w.stripeProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      {t('Processing...')}
                    </>
                  ) : (
                    t('Update subscription')
                  )}
                </Button>
              )
            ) : !w.isTrial && w.step === STEP.NUMBER_SCREEN ? (
              <Button
                onClick={() => w.handlePaidUpdate()}
                disabled={!w.canGoNext || !w.planFeatureId}
                className={cn(
                  !w.canGoNext || !w.planFeatureId
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
              >
                {t('Update')}
              </Button>
            ) : w.contextMinPlan && w.step === STEP.PLAN ? (
              <Button
                onClick={() => w.goNext()}
                disabled={
                  !w.canGoNext || w.validatingPlan || w.selectedPlan === w.currentPlanFeature
                }
                className={cn(
                  !w.canGoNext || w.validatingPlan || w.selectedPlan === w.currentPlanFeature
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
              >
                {w.validatingPlan ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('Checking...')}
                  </>
                ) : (
                  t('Change')
                )}
              </Button>
            ) : (
              <Button
                onClick={() => w.goNext()}
                disabled={!w.canGoNext || w.validatingPlan}
                className={cn(
                  !w.canGoNext || w.validatingPlan
                    ? 'opacity-50 cursor-not-allowed'
                    : 'cursor-pointer',
                )}
              >
                {w.validatingPlan ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('Checking...')}
                  </>
                ) : (
                  t('Next')
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Screen Management dialog — opens when user tries to decrease below min */}
      <ScreenManagementDialog
        open={w.showScreenManagement}
        onClose={() => w.setShowScreenManagement(false)}
        totalPairedScreens={w.minScreens}
        subAccountId={w.contextSubAccountId}
        teamId={w.contextTeamId}
      />

      {/* Plan change warning dialog */}
      <ChangePlanWarningDialog
        open={w.showChangePlanWarning}
        warnings={w.changePlanWarnings}
        confirmLabel={w.changePlanWarningConfirmLabel}
        onDismiss={w.dismissChangePlanWarning}
        onConfirm={w.confirmChangePlanWarning}
      />

      {/* TypeForm cancel subscription dialog */}
      <CancelSubscriptionDialog
        open={showCancelDialog}
        onClose={(submitted) => {
          setShowCancelDialog(false);
          if (submitted) {
            w.handleOpenChange(false);
            // Refetch to pick up scheduleCancel from webhook (async, may take a few seconds)
            setTimeout(() => {
              apolloClient.refetchQueries({
                include: w.contextSubAccountId
                  ? ['GetSubAccountInfo', 'GetSubAccountList']
                  : w.contextTeamId
                    ? ['GetTeamBillingInfo', 'getAllTeams']
                    : ['GetCompanyInfo'],
              });
            }, 2000);
          }
        }}
        subAccountId={w.contextSubAccountId}
      />
    </Dialog>
  );
}
